import type { CommandPaletteService } from '.';
import type { CommandEntry, CommandQueryRequest, CommandSubject, PaletteEntry, SymbolCardEntry } from './protocol';
import { Signal, computed } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { inject, injectable } from 'src/features/ioc';
import type { ReactBindings } from 'src/types/bindings';

type Log = ReturnType<ReactBindings['Logger']['getSubLogger']>;

interface ResolvedSymbolInfo {
    symbol: string;
    name?: string;
    exchange?: string;
}

@injectable()
export class CommandPaletteServiceImpl implements CommandPaletteService {
    #isOpen = new Signal(false);
    #query = new Signal('');
    #entries = new Signal<CommandEntry[]>([]);
    #explicitSubject = new Signal<CommandSubject | null>(null);
    #detectedSubject = new Signal<CommandSubject | null>(null);
    #detectedInfo = new Signal<ResolvedSymbolInfo | null>(null);
    #subject: ReadonlySignal<CommandSubject | null>;
    #infoEntries: ReadonlySignal<readonly PaletteEntry[]>;
    #sortedCommandEntries: ReadonlySignal<readonly CommandEntry[]>;

    #workspaceService: ReactBindings['WorkspaceService'];
    #securityMasterService: ReactBindings['SecurityMasterService'];
    #log: Log;

    #currentAbortController: AbortController | null = null;

    constructor(
        @inject('WorkspaceService') workspaceService: ReactBindings['WorkspaceService'],
        @inject('SecurityMasterService') securityMasterService: ReactBindings['SecurityMasterService'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#workspaceService = workspaceService;
        this.#securityMasterService = securityMasterService;
        this.#log = logger.getSubLogger({ name: 'CommandPaletteServiceImpl' });

        this.#subject = computed(() => this.#explicitSubject.value ?? this.#detectedSubject.value);

        this.#sortedCommandEntries = computed(() => {
            return this.#entries.value
                .values()
                .toArray()
                .sort((a, b) => b.score - a.score);
        });

        this.#infoEntries = computed(() => {
            const subject = this.#subject.value;
            if (!subject || subject.type !== 'symbol') return [];
            const info = this.#detectedInfo.value;
            const entry: SymbolCardEntry = {
                type: 'symbol-card',
                id: `symbol-card:${subject.value}`,
                source: 'symbol',
                score: 1.0,
                symbol: subject.value,
                name: info?.symbol === subject.value ? info.name : undefined,
                exchange: info?.symbol === subject.value ? info.exchange : undefined,
            };
            return [entry];
        });
    }

    get isOpen(): ReadonlySignal<boolean> {
        return this.#isOpen;
    }

    get query(): ReadonlySignal<string> {
        return this.#query;
    }

    get entries(): ReadonlySignal<readonly CommandEntry[]> {
        return this.#sortedCommandEntries;
    }

    get infoEntries(): ReadonlySignal<readonly PaletteEntry[]> {
        return this.#infoEntries;
    }

    get subject(): ReadonlySignal<CommandSubject | null> {
        return this.#subject;
    }

    get explicitSubject(): ReadonlySignal<CommandSubject | null> {
        return this.#explicitSubject;
    }

    open(): void {
        this.#isOpen.value = true;
        this.#query.value = '';
        this.#explicitSubject.value = null;
        this.#detectedSubject.value = null;
        this.#detectedInfo.value = null;
        this.#queryCommands('');
    }

    close(): void {
        this.#isOpen.value = false;
        this.#currentAbortController?.abort();
        this.#currentAbortController = null;
        this.#query.value = '';
        this.#explicitSubject.value = null;
        this.#detectedSubject.value = null;
        this.#detectedInfo.value = null;
        this.#entries.value = [];
    }

    toggle(): void {
        if (this.#isOpen.value) {
            this.close();
        } else {
            this.open();
        }
    }

    setQuery(query: string): void {
        this.#query.value = query;
        this.#queryCommands(query);
    }

    setSubject(subject: CommandSubject | null): void {
        this.#explicitSubject.value = subject;
        // Re-run query with the new subject/mode
        this.#queryCommands(this.#query.value);
    }

    promoteCurrentSymbol(): void {
        const current = this.#subject.peek();
        if (!current || current.type !== 'symbol') return;
        this.#explicitSubject.value = current;
        this.#query.value = '';
        this.#queryCommands('');
    }

    executeCommand(entry: CommandEntry): void {
        this.#workspaceService.executeCommand(entry.action);
        this.close();
    }

    #queryCommands(query: string): void {
        this.#currentAbortController?.abort();

        const controller = new AbortController();
        this.#currentAbortController = controller;
        const { signal } = controller;

        this.#entries.value = [];

        // Detection (async) runs only when there is no explicit subject. Explicit subject
        // supersedes detection.
        if (this.#explicitSubject.peek() === null) {
            this.#detectedSubject.value = null;
            this.#detectedInfo.value = null;
            void this.#runDetection(query, signal).then(() => {
                if (signal.aborted) return;
                // Re-issue once the detection completes so workspace can return symbol-scoped
                // entries for the now-known subject.
                if (this.#detectedSubject.peek()) {
                    this.#issueQuery(query, signal);
                }
            });
        } else {
            // Explicit mode: clear detection state so it doesn't linger.
            this.#detectedSubject.value = null;
            this.#detectedInfo.value = null;
        }

        this.#issueQuery(query, signal);
    }

    #issueQuery(query: string, signal: AbortSignal): void {
        if (signal.aborted) return;
        const subject = this.#subject.peek() ?? undefined;
        const mode = this.#explicitSubject.peek() ? 'explicit' : 'implicit';
        const requestId = crypto.randomUUID();
        const request: CommandQueryRequest = {
            type: 'command-query-request',
            requestId,
            query,
            subject,
            mode,
        };

        const accumulated: CommandEntry[] = [];
        const consume = async () => {
            for await (const response of this.#workspaceService.handleCommandQuery(request, signal)) {
                if (signal.aborted) return;
                for (const e of response.entries) {
                    if (e.type === 'command') accumulated.push(e);
                }
                // Only publish if this is the still-current controller.
                if (this.#currentAbortController !== null && !this.#currentAbortController.signal.aborted) {
                    this.#entries.value = [...accumulated];
                }
            }
        };

        consume().catch((err) => {
            if (!signal.aborted) this.#log.error('command query failed', err);
        });
    }

    async #runDetection(query: string, signal: AbortSignal): Promise<void> {
        const trimmed = query.trim();
        if (!trimmed) return;

        try {
            const entry = await this.#securityMasterService.fetchSecurityMasterEntry(trimmed);
            if (signal.aborted) return;
            if (entry && entry.symbol.toLowerCase() === trimmed.toLowerCase()) {
                const symbol = entry.symbol;
                this.#detectedSubject.value = { type: 'symbol', value: symbol };
                this.#detectedInfo.value = { symbol, name: entry.name, exchange: entry.exchange };
            }
        } catch {
            // Symbol not found or fetch error — leave detected subject as null.
        }
    }

    [Symbol.dispose](): void {
        this.#currentAbortController?.abort();
    }
}
